<template>
  <div :class="'scanner-container ' + (waitingForClose ? 'scanner-container--frozen' : '')">
    <div :class="'video-wrapper ' + (waitingForClose ? 'video-wrapper--frozen' : '')" v-show="scanning || starting">
      <video
        ref="video"
        autoplay
        playsinline
        muted
        class="scanner-video"
      ></video>
      <div class="scanner-overlay">
        <div :class="'scanner-area ' + (partialOk ? 'scanner-area--partial-ok' : '')">
          <!-- <div class="scanner-indicator"></div> -->
          <div v-if="waitingForClose" class="scanner-result-scanned">
            <pre>{{ formattedResult }}</pre>
          </div>
        </div>
      </div>
    </div>
    <div :class="'scanner-loading ' + (loading ? 'scanner-loading--loading' : '')" v-if="false && !error && scanning">
      Scanning...
    </div>
    <div v-if="error" class="scanner-error">
      {{ error }}
    </div>
    <div class="scanner-result">
      <!-- {{ formattedResult }} -->
      <div v-if="false && scanning">
        {{ scanRawResult }}
      </div>
      <div v-if="!starting && !scanning && !error">
        <button @click="startCamera()">Starta om skanning</button>
      </div>
    </div>
  </div>
</template>

<script>
// Tesseract is imported outside this script
export default {
  name: 'Scanner',
  // props
  props: {
    onSuccess: {
      type: Function,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      stream: null,
      scanResult: '',
      scanInterval: null,
      scanning: false,
      scanRawResult: '',
      isOpen: true,
      starting: false,
      partialOk: false,
      waitingForClose: false,
    };
  },
  mounted() {
    this.starting = true;
    this.startCamera();
  },
  // Watch isOpen
  beforeDestroy() {
    console.log('Shutting down scanner...');
    this.stopCamera();
    if (this.scanInterval) clearInterval(this.scanInterval);
  },
  computed: {
    formattedResult() {
      const data = this.scanResult;

      if (data.length === 23) {
        // Format as XX-XXXX-XXXX-XXXX-XXXX-XXXX-X
        return `${data.slice(0, 2)}-${data.slice(2, 6)}-${data.slice(6, 10)}-${data.slice(10, 14)}-${data.slice(14, 18)}-${data.slice(18, 22)}-${data.slice(22)}`;
      }

      if (data.length < 12) {
        return '';
      }

      return '';
      // return `Del av kortnumret: ${data}`;
    },
  },
  methods: {
    shutdown() {
      this.stopCamera();
    },
    async startCamera() {
      try {
        this.error = null;
        const constraints = {
          video: { facingMode: 'environment' },
        };
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.$refs.video.srcObject = this.stream;
        await this.$refs.video.play();
        this.starting = false;
        this.startScanning();
      } catch (e) {
        this.error = 'Kontrollera att kameran har tillåtelse att användas i webbläsaren.';
        this.scanning = false;
        this.starting = false;
      }
    },
    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      this.scanning = false;
    },
    startScanning() {
      // Scan every 0.7 seconds
      this.scanInterval = setInterval(this.captureAndScan, 700);
      this.scanning = true;
    },
    stopScanning() {
      clearInterval(this.scanInterval);
      this.stopCamera();
      this.waitingForClose = false;
    },
    async captureAndScan() {
      if (!this.$refs.video || this.loading) return;
      if (this.waitingForClose) {
        return;
      }

      const video = this.$refs.video;
      // Get video dimensions
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) return;
      // Calculate scanning area (center 95% width, 10% height)
      const scanW = Math.floor(w * 0.95);
      const scanH = Math.floor(h * 0.1);
      const scanX = Math.floor((w - scanW) / 2);
      const scanY = Math.floor((h - scanH) / 2);
      // Draw video frame to canvas
      const canvas = document.createElement('canvas');
      canvas.width = scanW;
      canvas.height = scanH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, scanX, scanY, scanW, scanH, 0, 0, scanW, scanH);
      // Preprocess: grayscale & contrast (optional, for better OCR)
      const imageData = ctx.getImageData(0, 0, scanW, scanH);
      for (let i = 0; i < imageData.data.length; i += 4) {
        const avg = (imageData.data[i] + imageData.data[i + 1] + imageData.data[i + 2]) / 3;
        imageData.data[i] = avg;
        imageData.data[i + 1] = avg;
        imageData.data[i + 2] = avg;
      }
      ctx.putImageData(imageData, 0, 0);
      // OCR
      this.loading = true;
      try {
        const { data: { text } } = await window.Tesseract.recognize(canvas.toDataURL('image/jpeg', 0.7), 'eng');

        this.scanRawResult = text;
        // console.log('OCR RAW result:', text);

        // Remove all non-numeric characters
        const numbers = text.replace(/\D/g, '');
        let match = null;
        if ((numbers).length === 20 + 2 + 1) {
          match = [numbers];
        }

        this.partialOk = false;

        if (match) {
          this.scanResult = match[0];

          if (this.onSuccess) {
            this.waitingForClose = true;
            this.onSuccess(this.formattedResult.replace(/-/g, ''));
          }
        } else if (numbers.length >= 12) {
          this.scanResult = numbers;
          this.partialOk = true;
        }
      } catch (err) {
        // OCR error, ignore and continue
      }
      this.loading = false;
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '~@/global.styl'

.scanner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%; /* Changed from 100vw to 100% */
  /* min-height: 100vh; */
  /* background: #111; */
  padding: 1rem;
  transition: all 1s ease;
  max-height: 300vh;
}

.scanner-container--frozen {
  max-height: 0;
}

.video-wrapper {
  position: relative;
  width: calc(100vw - 2rem);
  max-width: 480px;
  aspect-ratio: 1;
  background: #222;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.5);
  transition: all 1s ease;
}

.video-wrapper--frozen {
  background-color: #ffffff;

  video {
    visibility: hidden;
  }
}

.scanner-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
}

.scanner-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scanner-area {
  position: relative;
  width: 95%;
  height: 10%;
  border: 3px solid #5aa478;
  border-radius: 12px;
  box-sizing: border-box;
  background: rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 100vw 100vw #00000040;
}

@keyframes pulse-color {
  0% {
    border-color: #5aa478;
  }
  50% {
    border-color: #a3d9b1;
  }
  100% {
    border-color: #5aa478;
  }
}

.scanner-area--partial-ok {
  animation: pulse-color 1s infinite;
}

/* .scanner-indicator {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: rgba(0,234,255,0.5);
  box-shadow: 0 0 8px #00eaff;
} */

.scanner-loading {
  margin-top: 24px;
  color: #fff;
  font-size: 1.2em;
  transition: transform 0.3s ease;
}
.scanner-error {
  warning-box()
  width 100%
}
.scanner-loading--loading {
  transform: scale(1.1);
}
.scanner-result {
  /* color: white; */
}

.scanner-result-scanned {
  font-size: 1vw;
  color: #5aa478;
  zoom: 1.2;
  transition: all 0.3s;
}
</style>



// WEBPACK FOOTER //
// Scanner.vue?22655a3a